import { useEffect, useState } from 'react'

import { getCurrentStatusMplus } from 'widgets/mplus/mplus-payment/lib/getCurrentStatusMplus'
import {
  MplusStatuses,
  MplusStatusesTypes,
} from 'widgets/mplus/mplus-payment/module'

import { CalculatedMplusInfo, ICreditInfo } from 'entities/mplus/module'
import { mplusStore } from 'entities/mplus/module/mplus-store'
import userStore from 'entities/user/module/user-store'

import { CreditInfo } from 'shared/api/mplus/credit-info'
import { MIN_MPLUS_PRICE, isShowingMplus } from 'shared/constants'

interface Params {
  price: number
  tranchePrice?: string
  countPassengers: number
  isMplusAvailable?: boolean
}

// Resolves the M+ state for the current order: availability gates,
// the user's credit line and the installment calculation.
export const useMplusStatus = ({
  price,
  tranchePrice,
  countPassengers,
  isMplusAvailable,
}: Params) => {
  const [status, setStatus] = useState<MplusStatusesTypes>(
    MplusStatuses.INCLUDE.key,
  )
  const [creditInfo, setCreditInfo] = useState<ICreditInfo | null>(null)
  const [calculatedInfo, setCalculatedInfo] = useState<CalculatedMplusInfo>()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isActual = true

    const resolveStatus = async () => {
      if (
        !isShowingMplus ||
        !isMplusAvailable ||
        !userStore.getUserSettings()?.is_mplus_available
      ) {
        setStatus(MplusStatuses.NOT_ALLOW.key)
        return
      }

      if (!userStore.getAccountInfo()?.is_mplus_available) {
        setStatus(MplusStatuses.NOT_ALLOW_MPLUS.key)
        return
      }

      let userCreditInfo = mplusStore.getMplusInfo()

      // userCreditInfo = {
      //   status: 'ACTIVE',
      //   credit_line_id: '123123',
      //   created_at: '123123',
      //   message: '123123',
      //   amount: '50000',
      //   available_amount: '50000',
      // }

      // mplusStore.setCreditInfo({
      //   status: 'ACTIVE',
      //   credit_line_id: '123123',
      //   created_at: '123123',
      //   message: '123123',
      //   amount: '50000',
      //   available_amount: '50000',
      // })

      if (!userCreditInfo) {
        try {
          const { data: response } = await CreditInfo.getCreditInfo()

          userCreditInfo = response?.data
          mplusStore.setCreditInfo(response?.data)
        } catch {
          if (!isActual) return

          // No credit line: the user can only connect M+
          setStatus(
            price < MIN_MPLUS_PRICE
              ? MplusStatuses.NOT_ALLOW_PRICE.key
              : MplusStatuses.INCLUDE.key,
          )
          return
        }
      }

      if (!isActual) return

      const nextStatus = getCurrentStatusMplus({
        priceRequest: parseInt(tranchePrice || '0'),
        mplusStatus: userCreditInfo?.status as string,
        userRole: userStore.getAccountInfo()?.mbank_user_role as string,
        availableAmountMplus: parseInt(userCreditInfo?.available_amount || '0'),
      })

      // userCreditInfo = { status: 'ACTIVE', amount: '100' }
      // mplusStore.setCreditInfo({ status: 'ACTIVE', amount: '100' })

      setCreditInfo(userCreditInfo)
      setStatus(nextStatus)

      if (
        nextStatus !== MplusStatuses.ALLOW.key ||
        !tranchePrice ||
        !countPassengers
      ) {
        return
      }

      try {
        const { data: response } = await CreditInfo.getCalculatedInfo({
          price,
          quantity: countPassengers,
        })

        if (isActual) setCalculatedInfo(response?.data)
      } catch {}
    }

    setIsLoading(true)

    void resolveStatus().finally(() => {
      if (isActual) setIsLoading(false)
    })

    return () => {
      isActual = false
    }
  }, [price, tranchePrice, countPassengers, isMplusAvailable])

  return { status, creditInfo, calculatedInfo, isLoading }
}
